import { AuthenticatedHttpClient } from "@/lib/axios";
import {
  IpReputationResponse,
  PostmasterDomainsResponse,
  SpamRateResponse,
  DomainReputationResponse,
  AuthenticationResponse,
  EncryptionResponse,
  DeliveryErrorResponse,
  FeedbackLoopResponse,
  DnsCheckResponse,
  PostmasterDnsRecordsResponse,
  PostmasterConnectionStatus,
  DnsRecordsResponse,
  ComplianceResponse,
} from "@/types/postman.types";

export interface PostmasterDomain {
  domain: string;
  companyId: string;
  lastSyncedDate: string;
}
interface PostmasterStats {
  totalDomains: {
    count: number;
    verified: number;
  };
  highReputation: {
    count: number;
    percentage: number;
  };
  goodSpamRate: {
    count: number;
    threshold: string;
  };
  needsAttention: {
    count: number;
    threshold: string;
  };
}
export class PostmasterService {
  private http: AuthenticatedHttpClient;

  constructor(httpClient: AuthenticatedHttpClient) {
    this.http = httpClient;
  }

  async generateAuthUrl() {
    const response = await this.http.get("/postmaster/auth-url");
    return response as unknown as { url: string; accountId: string };
  }

  async handleAuthCallback(code: string, accountId: string) {
    const response = await this.http.post("/postmaster/auth-callback", {
      code,
      accountId,
    });
    return response;
  }

  async getMonitoredDomains(): Promise<PostmasterDomain[]> {
    const { data } = await this.http.get<PostmasterDomain[]>(
      "/postmaster/domains"
    );
    return data;
  }

  async getDnsRecordsWithStats(
    page: number,
    limit: number,
    search?: string,
    labels?: string[],
    selectedEspTypes?: string[]
  ) {
    const data = await this.http.post(
      `/postmaster/dns-records?page=${page}&limit=${limit}&search=${search}`,
      {
        labels,
        selectedEspTypes,
      }
    );
    return data as unknown as PostmasterDnsRecordsResponse;
  }

  async getDomainImage(domain: string) {
    const response = await this.http.post(`/postmaster/domains/image/abcd`, {
      domain,
    });

    console.log("response-chinmay", response);
    return response as unknown as string;
  }
  async getStats() {
    const response = await this.http.get(`/postmaster/stats`);
    return response as unknown as PostmasterStats;
  }
  async getDomainsWithLatestData(
    page: number,
    limit: number,
    search?: string,
    isSynced: boolean = false,
    labels?: string[]
  ) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      isSynced: isSynced.toString(),
    });

    if (search) {
      params.append("search", search);
    }

    // if (isSynced) {
    //   params.append("isSynced", isSynced.toString());
    // }

    const response = await this.http.post(
      `/postmaster/domains/latest?${params}`,
      {
        labels,
      }
    );
    return response as unknown as PostmasterDomainsResponse;
  }

  async disconnect() {
    const response = await this.http.post("/postmaster/disconnect");
    return response as unknown as boolean;
  }

  async isConnected() {
    const response = await this.http.get("/postmaster/is-connected");
    return response as unknown as boolean;
  }

  async getSpamRateData(domain: string, startDate: Date, endDate: Date) {
    const response = await this.http.get(
      `/postmaster/domains/${domain}/spam-data?startDate=${
        startDate.toISOString().split("T")[0]
      }&endDate=${endDate.toISOString().split("T")[0]}`
    );
    return response as unknown as SpamRateResponse;
  }

  async getIpReputationData(domain: string, startDate: Date, endDate: Date) {
    const response = await this.http.get(
      `/postmaster/domains/${domain}/ip-data?startDate=${
        startDate.toISOString().split("T")[0]
      }&endDate=${endDate.toISOString().split("T")[0]}`
    );
    return response as unknown as IpReputationResponse;
  }

  async getDomainReputationData(
    domain: string,
    startDate: Date,
    endDate: Date
  ) {
    const response = await this.http.get(
      `/postmaster/domains/${domain}/domain-data?startDate=${
        startDate.toISOString().split("T")[0]
      }&endDate=${endDate.toISOString().split("T")[0]}`
    );
    return response as unknown as DomainReputationResponse;
  }

  async getAuthenticationData(domain: string, startDate: Date, endDate: Date) {
    const response = await this.http.get(
      `/postmaster/domains/${domain}/auth-data?startDate=${
        startDate.toISOString().split("T")[0]
      }&endDate=${endDate.toISOString().split("T")[0]}`
    );
    return response as unknown as AuthenticationResponse;
  }

  async getEncryptionData(domain: string, startDate: Date, endDate: Date) {
    const response = await this.http.get(
      `/postmaster/domains/${domain}/encryption-data?startDate=${
        startDate.toISOString().split("T")[0]
      }&endDate=${endDate.toISOString().split("T")[0]}`
    );
    return response as unknown as EncryptionResponse;
  }

  async getDeliveryErrorData(domain: string, startDate: Date, endDate: Date) {
    const response = await this.http.get(
      `/postmaster/domains/${domain}/delivery-error-data?startDate=${
        startDate.toISOString().split("T")[0]
      }&endDate=${endDate.toISOString().split("T")[0]}`
    );
    return response as unknown as DeliveryErrorResponse;
  }

  async getFeedbackLoopData(domain: string, startDate: Date, endDate: Date) {
    const response = await this.http.get(
      `/postmaster/domains/${domain}/feedback-loop-data?startDate=${
        startDate.toISOString().split("T")[0]
      }&endDate=${endDate.toISOString().split("T")[0]}`
    );
    return response as unknown as FeedbackLoopResponse;
  }

  async getComplianceData(
    domain: string,
    startDate?: Date,
    endDate?: Date
  ) {
    let url = `/postmaster/domains/${domain}/compliance-data`;
    
    // Only add date params if both are provided (dates are optional for compliance)
    if (startDate && endDate) {
      url += `?startDate=${startDate.toISOString().split("T")[0]}&endDate=${endDate.toISOString().split("T")[0]}`;
    }
    
    const response = await this.http.get(url);
    return response as unknown as ComplianceResponse;
  }

  async exportDomainsData() {
    return this.http.get("/postmaster/domains/export", {
      responseType: "blob",
    });
  }

  async exportDnsRecordsData() {
    return this.http.get("/postmaster/dns-records/export", {
      responseType: "blob",
    });
  }

  async getDnsCheckData(domain: string) {
    const response = await this.http.get(
      `/postmaster/domains/${domain}/dns-check`
    );
    return response as unknown as DnsCheckResponse;
  }

  async getConnectionStatus() {
    const response = await this.http.get("/postmaster/connection-status");
    return response as unknown as PostmasterConnectionStatus;
  }

  async disconnectAccount(accountId: string) {
    const response = await this.http.post(`/postmaster/disconnect`, {
      accountId,
    });
    return response as unknown as boolean;
  }

  async getStoredDnsRecords(domain: string, espType: string) {
    const response = await this.http.get(
      `/postmaster/domains/${domain}/dns-records/${espType}`
    );
    return response as unknown as DnsRecordsResponse;
  }

  // async runDnsCheck(domain: string) {
  //   const response = await this.http.post(
  //     `/postmaster/domains/${domain}/dns-check`
  //   );
  //   await this.runDomainHealthCheck(domain);
  //   return response as unknown as DnsCheckResponse;
  // }

  async runDomainHealthCheck(domain: string, espType?: string) {
    const response = await this.http.post(`/mail-sender/domain-health-check`, {
      domain,
      espType,
    });
    return response as unknown as DnsCheckResponse;
  }

  async updateDomainsSyncStatus(domains: string[], isSynced: boolean) {
    const response = await this.http.post("/postmaster/sync-status", {
      domains,
      isSynced,
    });
    return response as unknown as boolean;
  }

  async addLabelsToPostmasterDomain(domain: string[], labelIds: string[]) {
    const response = await this.http.post(`/postmaster/labels`, {
      domain,
      labelIds,
    });
    return response as unknown as boolean;
  }

  async removeLabelsFromDomains(domains: string[], labelIds: string[]) {
    const response = await this.http.delete(`/postmaster/domains/labels`, {
      data: {
        domains,
        labelIds,
      },
    });
    return response as unknown as boolean;
  }
  async addDomainToDns(domain: string[], labelIds: string[]) {
    const response = await this.http.post(`/postmaster/dns-records/labels`, {
      domain,
      labelIds,
    });
    return response as unknown as boolean;
  }
  async removeDomainFromDns(domain: string[], labelIds: string[]) {
    const response = await this.http.delete(`/postmaster/dns-records/labels`, {
      data: {
        domain,
        labelIds,
      },
    });
    return response as unknown as boolean;
  }

  async getEmailTestStatus(domain: string) {
    const response = await this.http.get(
      `/mail-sender/domain-health-check/${domain}`
    );
    return response as unknown;
  }

  // ------------------------- Publish Report -------------------------

  async publishReport(domain: string, ids: string[]) {
    const response = await this.http.post("/postmaster/publish-record", {
      domain,
      ids,
    });
    return response as unknown as string[];
  }

  async unPublishReport(domain: string, ids: string[]) {
    const response = await this.http.post("/postmaster/unpublish-record", {
      domain,
      ids,
    });
    return response as unknown as boolean;
  }

  async publishDomainReport(domains: string[]) {
    const response = await this.http.post("/postmaster/publish/all", {
      domains,
    });
    return response as unknown as string[];
  }

  async unPublishDomainReport(domains: string[]) {
    const response = await this.http.post("/postmaster/unpublish/all", {
      domains,
    });
    return response as unknown as boolean;
  }
}
